import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Divider,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import moment from "moment";
import { BASEURL } from "api/axiosInstance";

const RowItem = ({
  label,
  value,
  isBold = false,
  isRed = false,
  isGrey = false,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 0.5,
      px: 2,
    }}
  >
    <Typography
      variant={isBold ? "subtitle1" : "body1"}
      sx={{
        fontWeight: isBold ? "bold" : "normal",
        color: isRed
          ? "error.main"
          : isGrey
          ? "text.secondary"
          : "text.primary",
        flex: 1,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant={isBold ? "subtitle1" : "body1"}
      sx={{
        fontWeight: isBold ? "bold" : "normal",
        color: isRed
          ? "error.main"
          : isGrey
          ? "text.secondary"
          : "text.primary",
        textAlign: "right",
        flex: 1,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const formatRupiah = (amount) => {
  if (amount === null || amount === undefined) return "Rp 0";
  const isNegative = amount < 0;
  const absoluteAmount = Math.abs(amount);
  const formatted = absoluteAmount.toLocaleString("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return `Rp ${formatted}${isNegative ? " (-)" : ""}`;
};

const DetailSetoranModalTransfer = ({
  open,
  onClose,
  dataDetail,
  dataPengeluaran,
  onSubmit,
}) => {
  const [pendapatan, setPendapatan] = React.useState({
    transfer: "",
    list_pendapatan: [],
  });

  const mapValueToCount = (value, nominal) =>
    value ? (value / nominal).toString() : "";

  React.useEffect(() => {
    if (!open || !dataPengeluaran) return;
    const dataPendapatan = dataPengeluaran.list_pendapatan;
    if (dataPendapatan && dataPendapatan.tipe === "pendapatan") {
      const loadedPecahan = {
        transfer:
          dataPendapatan.transfer != null
            ? String(dataPendapatan.transfer)
            : "",
      };
      const parsedList = deserializeList(dataPendapatan.list ?? "");
      setPendapatan({
        ...loadedPecahan,
        list_pendapatan: parsedList,
      });
    } else {
      setPendapatan({ transfer: "", list_pendapatan: [] });
    }
  }, [dataPengeluaran, open]);

  const handleAddList = () => {
    setPendapatan((prev) => ({
      ...prev,
      list_pendapatan: [...prev.list_pendapatan, { keterangan: "", value: 0 }],
    }));
  };

  const handleChangeList = (idx, field, newValue) => {
    const updated = [...pendapatan.list_pendapatan];
    updated[idx][field] = field === "value" ? Number(newValue) || 0 : newValue;
    setPendapatan((prev) => ({ ...prev, list_pendapatan: updated }));
  };

  const handleRemoveList = (idx) => {
    const updated = [...pendapatan.list_pendapatan];
    updated.splice(idx, 1);
    setPendapatan((prev) => ({ ...prev, list_pendapatan: updated }));
  };

  const hitungTotalTransfer = () => {
    return parseInt(pendapatan.transfer || 0);
  };

  const hitungTotalList = () => {
    return (pendapatan.list_pendapatan || []).reduce(
      (acc, item) => acc + (Number(item.value) || 0),
      0
    );
  };

  const totalTransfer = hitungTotalTransfer();
  const totalList = hitungTotalList();
  const isValid = totalTransfer === totalList && totalTransfer > 0;

  const serializeList = (list) => {
    if (!Array.isArray(list)) return "";
    return list
      .filter((item) => item.keterangan && typeof item.value === "number")
      .map((item) => `${item.keterangan}: ${item.value}`)
      .join("; ");
  };

  const deserializeList = (str) => {
    if (!str || typeof str !== "string") return [];
    return str
      .split(";")
      .map((item) => item.trim())
      .filter((item) => item.length > 0)
      .map((item) => {
        const colonIndex = item.lastIndexOf(":");
        if (colonIndex === -1) return null;
        const keterangan = item.substring(0, colonIndex).trim();
        const valueStr = item.substring(colonIndex + 1).trim();
        let value = parseFloat(valueStr.replace(/\D/g, ""));
        if (isNaN(value)) value = 0;
        return { keterangan, value };
      })
      .filter((item) => item !== null && item.keterangan !== "");
  };

  const [hasSignificantNegativeSelisih, setHasSignificantNegativeSelisih] =
    React.useState(false);

  React.useEffect(() => {
    if (
      !dataDetail?.original_data ||
      !Array.isArray(dataDetail.original_data)
    ) {
      setHasSignificantNegativeSelisih(false);
      return;
    }
    const isError = dataDetail.original_data.some((item) => {
      const calc =
        item.rp_omzet_kasir +
        item.rp_debit_credit +
        item.rp_ewallet +
        item.rp_pengeluaran;
      const selisih = calc - item.rp_omzet_data;
      return selisih <= -5000;
    });
    setHasSignificantNegativeSelisih(isError);
  }, [dataDetail]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const cleanValue = value.replace(/\D/g, "");
    setPendapatan((prev) => ({ ...prev, [name]: cleanValue }));
  };

  const isDataChanged = React.useCallback(() => {
    const dataAwal = dataPengeluaran?.list_pendapatan;
    if (!dataAwal) {
      return totalTransfer > 0 || totalList > 0;
    }
    const isCoinChanged =
      Number(pendapatan.transfer || 0) !== (dataAwal.transfer || 0);
    const stateListSerialized = serializeList(pendapatan.list_pendapatan || "");
    const initialList = dataAwal.list ?? "";
    const isListChanged = stateListSerialized !== initialList;
    return isCoinChanged || isListChanged;
  }, [pendapatan, dataPengeluaran, totalTransfer, totalList, serializeList]);

  const isEditMode = !!dataPengeluaran?.list_pendapatan;
  const isApprovalMode =
    !isEditMode && dataDetail?.status === "belum_disetujui";
  const isApproveAction = isApprovalMode && !hasSignificantNegativeSelisih;
  const dataIsChanged = isDataChanged();
  const isReadyToSubmit = React.useMemo(() => {
    if (isApproveAction) return true;
    if (!isValid) return false;
    return dataIsChanged;
  }, [isApproveAction, isValid, dataIsChanged]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          DETAIL SETORAN TRANSFER
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {dataDetail?.nama_cab} - {dataDetail?.id_cab}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
          {dataDetail?.id_karyawan} - {dataDetail?.nama_karyawan}
        </Typography>
        <Typography variant="body1" sx={{ mb: 0.5 }}>
          <strong>Sinkron:</strong>{" "}
          {dataDetail?.tgl_kas &&
            moment(dataDetail?.tgl_kas).format("DD MMMM YYYY")}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ px: 1 }}>
          <RowItem
            label="Jumlah Kasir"
            value={dataDetail?.original_data?.length?.toString() || "0"}
          />
          <RowItem
            label="Uang Cash"
            value={formatRupiah(dataDetail?.nominal_setoran)}
            isBold
          />
          <RowItem
            label="STL"
            value={
              dataDetail?.is_stl ? formatRupiah(dataDetail.total_stl) : "-"
            }
          />
          <RowItem
            label="EDC Machine"
            value={
              dataDetail?.is_edc
                ? formatRupiah(dataDetail.total_debit_credit)
                : "-"
            }
          />
          <RowItem
            label="Qris"
            value={
              dataDetail?.is_qris ? formatRupiah(dataDetail.total_ewallet) : "-"
            }
          />
          <RowItem
            label="Pengeluaran"
            value={
              dataDetail?.is_pengeluaran
                ? formatRupiah(dataDetail.total_pengeluaran)
                : "-"
            }
          />
          <Divider />
        </Box>
        <Box sx={{ px: 1 }}>
          <RowItem
            label="Subtotal Omzet"
            value={formatRupiah(dataDetail?.subtotal_kasir)}
            isBold
          />
          {dataDetail?.original_data?.map((item, index) => {
            const calc =
              item.rp_omzet_kasir +
              item.rp_debit_credit +
              item.rp_ewallet +
              item.rp_pengeluaran;
            const selisih = calc - item.rp_omzet_data;
            return (
              <RowItem
                key={index}
                label={`Selisih ${item.opr}`}
                value={formatRupiah(selisih)}
                isRed={selisih < 0}
              />
            );
          })}
          <RowItem
            label="Total Omzet"
            value={formatRupiah(dataDetail?.total_omzet_data)}
            isBold
          />
          <Divider sx={{ my: 0.5 }} />
        </Box>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: "bold" }}>
              Rincian Transfer
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <>
              <RowItem
                label="Jumlah Transfer"
                value={formatRupiah(dataDetail?.transfer)}
              />
              <RowItem label="Nomor Referensi" value={dataDetail?.no_ref} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 0.5,
                  px: 2,
                }}
              >
                <Typography
                  variant={"body1"}
                  sx={{
                    fontWeight: "normal",
                    color: "text.primary",
                    flex: 1,
                  }}
                >
                  Bukti Tranfer
                </Typography>
                <a
                  href={`${BASEURL}/photos/${dataDetail?.photo}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1976d2", textDecoration: "underline" }}
                >
                  Lihat
                </a>
              </Box>
            </>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: "bold" }}>Catatan</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Note STL:</strong> {dataDetail?.list_stl ?? "-"}
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Note EDC:</strong> {dataDetail?.is_edc ? "Ya" : "Tidak"}
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Note Qris:</strong> {dataDetail?.is_qris ? "Ya" : "Tidak"}
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>Note Pengeluaran:</strong>{" "}
              {dataDetail?.list_pengeluaran ?? "-"}
            </Typography>
            {dataDetail?.original_data?.length > 0 &&
              dataDetail?.original_data?.map((item, index) => {
                return (
                  <Typography key={`kembalian-${index}`} variant="body1">
                    <span
                      style={{ color: "#000" }}
                    >{`Kembali Uang ${item.opr}:`}</span>{" "}
                    <span style={{ color: "#888" }}>
                      {item.rp_ku == 0 ? "Rp. 0" : formatRupiah(item.rp_ku)}
                    </span>
                  </Typography>
                );
              })}
          </AccordionDetails>
        </Accordion>

        {/* Hanya Pendapatan */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: "bold" }}>Pendapatan</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Nominal Transfer
                  </Typography>
                  <TextField
                    name="transfer"
                    size="small"
                    fullWidth
                    value={pendapatan.transfer || ""}
                    onChange={handleChange}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mt: 1 }}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                mb={1}
                color="success.main"
              >
                LIST
              </Typography>

              {pendapatan.list_pendapatan.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    gap: 1,
                    mb: 1,
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    label="Keterangan"
                    value={item.keterangan}
                    onChange={(e) =>
                      handleChangeList(idx, "keterangan", e.target.value)
                    }
                  />
                  <TextField
                    size="small"
                    fullWidth
                    value={item.value}
                    onChange={(e) =>
                      handleChangeList(idx, "value", e.target.value)
                    }
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                  />
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => handleRemoveList(idx)}
                  >
                    Hapus
                  </Button>
                </Box>
              ))}

              <Button
                fullWidth
                size="small"
                variant="contained"
                onClick={handleAddList}
              >
                + Tambah List Pendapatan
              </Button>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                {`💵 Total Transfer: Rp ${totalTransfer.toLocaleString()}`}
              </Typography>
              <Typography variant="body2">
                🧾 Total List: Rp {totalList.toLocaleString()}
              </Typography>
            </Box>

            <Button
              fullWidth
              sx={{ mt: 2 }}
              variant="contained"
              color={isReadyToSubmit ? "success" : "error"}
              disabled={!isReadyToSubmit}
              onClick={() => {
                const currentDataList = dataPengeluaran?.list_pendapatan;
                const finalPayload = {
                  ...(dataPengeluaran &&
                    currentDataList && { id: currentDataList.id }),
                  id_cab: dataDetail?.id_cab,
                  nama_cab: dataDetail?.nama_cab,
                  seratus: 0,
                  limapuluh: 0,
                  duapuluh: 0,
                  sepuluh: 0,
                  limaribu: 0,
                  duaribu: 0,
                  seribu: 0,
                  coin: 0,
                  transfer: Number(pendapatan.transfer || 0),
                  nominal: totalTransfer,
                  list: serializeList(pendapatan.list_pendapatan),
                  tipe: "pendapatan",
                  waktu_setoran: dataDetail?.waktu_setoran,
                  tgl_kas: dataDetail?.tgl_kas,
                };
                const justApprove = isApproveAction ? true : false;
                onSubmit(finalPayload, justApprove);
              }}
            >
              {isApproveAction ? "SETUJUI" : isEditMode ? "UBAH" : "SIMPAN"}
            </Button>
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "center",
          p: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            fontWeight: "bold",
            flexGrow: 1,
            backgroundColor: (theme) => theme.palette.grey[300],
            color: (theme) => theme.palette.text.primary,
            "&:hover": {
              backgroundColor: (theme) => theme.palette.grey[400],
            },
          }}
        >
          TUTUP
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetailSetoranModalTransfer;
